import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { salesApi, apiError } from '../salesApi';
import { useT } from '../salesI18n';
import { useSalesAuth } from '../SalesAuthContext';
import { AddressAutocomplete } from './AddressAutocomplete';

const SOURCES = ['referral', 'website', 'phone', 'door_to_door', 'event', 'social', 'other'];

const empty = { customer_name: '', phone: '', email: '', address: '', city: '', state: 'GA', zip_code: '', formatted_address: '', lat: null, lng: null, source: 'referral', estimated_value: '', notes: '', assigned_to: '', year_built: '' };

export const LeadFormDialog = ({ open, onOpenChange, lead, onSaved }) => {
  const { t } = useT();
  const { user } = useSalesAuth();
  const [form, setForm] = useState(empty);
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(lead?.id);

  useEffect(() => {
    if (!open) return;
    setForm(lead ? { ...empty, ...lead, estimated_value: lead.estimated_value ?? '', year_built: lead.year_built ?? '', address: lead.formatted_address || lead.address || '' } : { ...empty, assigned_to: user?.username || '' });
    if (user?.role === 'manager') salesApi.get('/team').then((r) => setTeam(r.data)).catch(() => {});
  }, [open, lead, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { toast.error(`${t.customerName}: ${t.required}`); return; }
    setSaving(true);
    const payload = {
      customer_name: form.customer_name, phone: form.phone || null, email: form.email || null,
      address: form.address || null, city: form.city || null, state: form.state || null, zip_code: form.zip_code || null,
      formatted_address: form.formatted_address || form.address || null, lat: form.lat, lng: form.lng,
      source: form.source, estimated_value: form.estimated_value === '' ? null : Number(form.estimated_value),
      notes: form.notes || null, assigned_to: form.assigned_to || null, year_built: form.year_built === '' ? null : Number(form.year_built),
    };
    try {
      const { data } = isEdit ? await salesApi.put(`/leads/${lead.id}`, payload) : await salesApi.post('/leads', payload);
      toast.success(isEdit ? t.leadSaved : t.leadCreated);
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lead-form-dialog" className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? t.editLead : t.createLead}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>{t.customerName} *</Label>
            <Input data-testid="lead-name-input" value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} className="h-11 mt-1" autoFocus />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t.phone}</Label>
              <Input data-testid="lead-phone-input" value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} className="h-11 mt-1" inputMode="tel" />
            </div>
            <div>
              <Label>{t.email}</Label>
              <Input data-testid="lead-email-input" type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} className="h-11 mt-1" />
            </div>
          </div>
          <div>
            <Label>{t.address}</Label>
            <div className="mt-1">
              <AddressAutocomplete
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v, formatted_address: v }))}
                onSelect={(a) => setForm((f) => ({ ...f, address: a.address, formatted_address: a.formatted, city: a.city, state: a.state, zip_code: a.zip, lat: a.lat, lng: a.lng }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <Label>{t.city}</Label>
              <Input data-testid="lead-city-input" value={form.city || ''} onChange={(e) => set('city', e.target.value)} className="h-11 mt-1" />
            </div>
            <div>
              <Label>{t.state}</Label>
              <Input data-testid="lead-state-input" value={form.state || ''} onChange={(e) => set('state', e.target.value)} className="h-11 mt-1" maxLength={2} />
            </div>
            <div>
              <Label>{t.zip}</Label>
              <Input data-testid="lead-zip-input" value={form.zip_code || ''} onChange={(e) => set('zip_code', e.target.value)} className="h-11 mt-1" inputMode="numeric" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t.sourceLabel}</Label>
              <Select value={form.source} onValueChange={(v) => set('source', v)}>
                <SelectTrigger data-testid="lead-source-select" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s} value={s} data-testid={`lead-source-opt-${s}`}>{t.source[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t.estimatedValue} (US$)</Label>
              <Input data-testid="lead-value-input" type="number" min="0" step="1" value={form.estimated_value} onChange={(e) => set('estimated_value', e.target.value)} className="h-11 mt-1 font-mono" />
            </div>
          </div>
          <div>
            <Label>{t.yearBuilt}</Label>
            <Input data-testid="lead-year-built-input" type="number" min="1700" max={new Date().getFullYear() + 1} value={form.year_built} onChange={(e) => set('year_built', e.target.value)} className="h-11 mt-1 font-mono" placeholder="1985" />
            <p className="text-[11px] text-slate-500 mt-1">{t.yearBuiltHint}</p>
          </div>
          {user?.role === 'manager' && team.length > 0 && (
            <div>
              <Label>{t.assignedTo}</Label>
              <Select value={form.assigned_to || ''} onValueChange={(v) => set('assigned_to', v)}>
                <SelectTrigger data-testid="lead-assigned-select" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {team.map((m) => <SelectItem key={m.username} value={m.username}>{m.full_name} ({t.role[m.role] || m.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>{t.notes}</Label>
            <Textarea data-testid="lead-notes-input" value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={3} className="mt-1" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" className="h-11" onClick={() => onOpenChange(false)} data-testid="lead-form-cancel">{t.cancel}</Button>
            <Button type="submit" className="h-11 font-semibold" disabled={saving} data-testid="lead-form-submit">{saving ? t.loading : (isEdit ? t.save : t.create)}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
